import { useCallback, useEffect, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import {
  type GamesFilter,
  GamesFilterSortDirection,
  GamesFilterSortType,
} from '@/api/games';
import { RestrictedProviders } from '@/helpers/conf';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { useGetCommonStore } from '@/store';
import { CHUNK_SIZE_SEARCH } from '../helpers';

const PAGE_SIZE = CHUNK_SIZE_SEARCH * 2;

interface UseSearchGamesInfiniteParams {
  searchPhrase: string | undefined;
}

export const useSearchGamesInfinite = ({
  searchPhrase,
}: UseSearchGamesInfiniteParams) => {
  const { use } = useGetCommonStore();
  const isLiveSpinsAvailable = use.isLiveSpinsAvailable();
  const fetchGames = use.fetchGames();

  const getKey = (
    pageIndex: number,
    previousPageData: GamesFilter.Response | null
  ) => {
    if (!searchPhrase) return null;
    if (pageIndex === 0) return ['searchGames', searchPhrase, 1, PAGE_SIZE];
    if (!previousPageData || previousPageData.data.length < PAGE_SIZE)
      return null;

    return ['searchGames', searchPhrase, pageIndex + 1, PAGE_SIZE];
  };

  const {
    data: pages = [],
    size,
    setSize,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWRInfinite<GamesFilter.Response | undefined>(getKey, {
    fetcher: ([, phrase, page]) =>
      fetchGames({
        page,
        pageSize: PAGE_SIZE,
        filter: { title: phrase },
        sort: {
          type: GamesFilterSortType.POPULARITY,
          direction: GamesFilterSortDirection.ASC,
        },
      }),
    revalidateFirstPage: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    setSize(1);
  }, [searchPhrase, setSize]);

  const games = useMemo(
    () =>
      pages
        .flatMap((page) => (page ? mapGameV2ToGameV1(page.data) : []))
        .filter((game) => {
          const blockedLiveSpin = game.provider.includes(
            RestrictedProviders.LIVESPIN
          );
          const blockedAutopay = game.provider.includes(
            RestrictedProviders.AUTOPAY
          );
          return (!blockedLiveSpin || isLiveSpinsAvailable) && !blockedAutopay;
        }),
    [pages, isLiveSpinsAvailable]
  );

  const isLoadingMore =
    isLoading || (size > 0 && pages && typeof pages[size - 1] === 'undefined');

  const isEmpty = games.length === 0 && !isLoading;
  const pending = isLoading || isValidating;

  const loadMore = useCallback(() => {
    if (isLoadingMore) return;
    setSize((prev) => prev + 1);
  }, [isLoadingMore, setSize]);

  return {
    games,
    totalCount: pages[0]?.pagination?.totalCount,
    loadMore,
    isLoadingMore,
    isEmpty,
    pending,
    isLoading,
    error,
    mutate,
  };
};
